import {
  alpha,
  type ButtonProps,
  type Components,
  darken,
  lighten,
  type Theme,
} from '@mui/material';
import { ThemeConfig } from '../base-theme-config';
import { designTokens } from '../themes/design-tokens';
import { BUTTON_HEIGHTS, BUTTON_PADDING_MAP } from './button.enums';

const createSizeVariant =
  (size: ButtonProps['size'] = 'md') =>
  ({ theme }: ThemeConfig) => {
    const height = BUTTON_HEIGHTS[size];
    const paddingSize = theme.spacing(BUTTON_PADDING_MAP[size]);

    // Define typography based on design tokens
    const getTypographyForSize = () => {
      switch (size) {
        case 'sm':
          return designTokens.button.typography.sm;
        case 'md':
          return designTokens.button.typography.md;
        case 'lg':
          return designTokens.button.typography.lg;
        case 'xl':
          return designTokens.button.typography.xl;
        default:
          return designTokens.button.typography.md;
      }
    };

    const typography = getTypographyForSize();

    return {
      minHeight: `${height}px`,
      ...typography,
      paddingLeft: paddingSize,
      paddingRight: paddingSize,
      fontStyle: 'normal',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(Math.max(1, BUTTON_PADDING_MAP[size] - 1)),
        paddingRight: theme.spacing(Math.max(1, BUTTON_PADDING_MAP[size] - 1)),
        // Keep same typography on mobile
        ...typography,
      },
    };
  };

const createButtonVariant =
  (variant: ButtonProps['variant'] = 'primary') =>
  ({ theme }: ThemeConfig) => {
    const baseStyles = {
      border: 'none',
      transform: 'none',
      transition: theme.transitions.create(['all'], {
        duration: theme.transitions.duration.short,
      }),
      // Add focus and hover states for better UX
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.opposite,
          '&:hover': {
            backgroundColor: lighten(theme.palette.primary.main, 0.2),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            backgroundColor: darken(theme.palette.primary.main, 0.3),
          },
          '&:disabled': {
            backgroundColor: theme.palette.text.fifth,
            color: theme.palette.text.fourth,
            transform: 'none',
          },
        };
      case 'outlined':
        return {
          ...baseStyles,
          border: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          '&:hover': {
            color: alpha(theme.palette.primary.main, 0.6),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.6)}`,
            backgroundColor: alpha(theme.palette.background.button, 0.2),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: alpha(theme.palette.background.button, 0.3),
            transform: 'translateY(0)',
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            borderColor: theme.palette.text.disabled,
            color: theme.palette.text.disabled,
            transform: 'none',
          },
        };
      case 'naked':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          '&:hover': {
            color: alpha(theme.palette.primary.main, 0.6),
            backgroundColor: alpha(theme.palette.background.button, 0.2),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.background.button, 0.3),
            transform: 'translateY(0)',
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            color: theme.palette.text.disabled,
            transform: 'none',
          },
        };
      case 'link':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          textDecoration: 'underline',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
            color: alpha(theme.palette.primary.main, 0.6),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            color: theme.palette.text.main,
            textDecoration: 'underline',
            transform: 'translateY(0)',
          },
          '&:disabled': {
            color: theme.palette.text.disabled,
            textDecoration: 'underline',
          },
        };
      case 'accent':
        return {
          ...baseStyles,
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.text.main,
          '&:hover': {
            backgroundColor: lighten(theme.palette.secondary.main, 0.2),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            backgroundColor: darken(theme.palette.secondary.main, 0.3),
            transform: 'translateY(0)',
          },
          '&:disabled': {
            backgroundColor: theme.palette.text.fifth,
            color: theme.palette.text.fourth,
            transform: 'none',
          },
        };
      case 'action':
        return {
          ...baseStyles,
          backgroundColor: theme.palette.secondaryInvert.main,
          color: theme.palette.white,
          '&:hover': {
            backgroundColor: lighten(theme.palette.secondaryInvert.main, 0.2),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            backgroundColor: darken(theme.palette.secondaryInvert.main, 0.3),
          },
          '&:disabled': {
            backgroundColor: theme.palette.text.fifth,
            color: theme.palette.text.fourth,
            transform: 'none',
          },
        };
    }
  };

export const buttonTheme: Components<Theme> = {
  MuiButton: {
    styleOverrides: {
      root: ({ theme }: ThemeConfig) => ({
        borderRadius: theme.customShape.borderRadius4XL,
        fontFamily: theme.typography.fontFamily,
        minWidth: 'auto',
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        transition: theme.transitions.create(['all'], {
          duration: theme.transitions.duration.short,
        }),
        whiteSpace: 'nowrap' as const,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center' as const,
        textTransform: 'none' as const,
        cursor: 'pointer',
        wordBreak: 'keep-all',
        overflow: 'hidden',
        // Disable MUI's default ripple and overlays
        '& .MuiTouchRipple-root': {
          display: 'none',
        },
        '&::before, &::after': {
          display: 'none',
        },
        // Icon spacing
        '& .MuiButton-startIcon': {
          marginLeft: 0,
          marginRight: theme.spacing(2),
          '& > svg': {
            fontSize: 'inherit',
          },
        },
        '& .MuiButton-endIcon': {
          marginLeft: theme.spacing(2),
          marginRight: 0,
          '& > svg': {
            fontSize: 'inherit',
          },
        },
        '& *': {
          textAlign: 'inherit',
        },
        // Mobile responsiveness
        // [theme.breakpoints.down('sm')]: {
        //   paddingLeft: theme.spacing(4),
        //   paddingRight: theme.spacing(4),
        //   minWidth: '120px', // Minimum width on mobile for usability
        // },
        '&:disabled': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        },
      }),
    },
    variants: [
      {
        props: { variant: 'primary' },
        style: ({ theme }: ThemeConfig) => ({
          ...createButtonVariant('primary')({ theme }),
        }),
      },
      {
        props: { variant: 'outlined' },
        style: ({ theme }: ThemeConfig) => ({
          ...createButtonVariant('outlined')({ theme }),
        }),
      },
      {
        props: { variant: 'naked' },
        style: ({ theme }: ThemeConfig) => ({
          ...createButtonVariant('naked')({ theme }),
        }),
      },
      {
        props: { variant: 'link' },
        style: ({ theme }: ThemeConfig) => ({
          ...createButtonVariant('link')({ theme }),
        }),
      },
      {
        props: { variant: 'accent' },
        style: ({ theme }: ThemeConfig) => ({
          ...createButtonVariant('accent')({ theme }),
        }),
      },
      {
        props: { variant: 'action' },
        style: ({ theme }: ThemeConfig) => ({
          ...createButtonVariant('action')({ theme }),
        }),
      },
      {
        props: { size: 'sm' },
        style: ({ theme }: ThemeConfig) => ({
          ...createSizeVariant('sm')({ theme }),
        }),
      },
      {
        props: { size: 'md' },
        style: ({ theme }: ThemeConfig) => ({
          ...createSizeVariant('md')({ theme }),
        }),
      },
      {
        props: { size: 'lg' },
        style: ({ theme }: ThemeConfig) => ({
          ...createSizeVariant('lg')({ theme }),
        }),
      },
      {
        props: { size: 'xl' },
        style: ({ theme }: ThemeConfig) => ({
          ...createSizeVariant('xl')({ theme }),
        }),
      },
      {
        props: { fullWidth: true },
        style: ({ theme }: ThemeConfig) => ({
          width: '100%',
          [theme.breakpoints.up('sm')]: {
            width: 'auto',
            minWidth: '200px',
          },
        }),
      },
    ],
  },
};
